/**
 * Chat Action Creators
 * Contains functions for handling chat interactions, message history, and API requests
 * @module Chat/Actions
 */
import {
  ChatItemType,
  ChatMessageItemType,
  DevMessageItem,
  GetChatMessagesForm,
  LinksMetaData,
  QNAStreamingForm,
} from '../../../interfaces/api/qna';
import { tr } from '../../../translation';
import {
  createNewChat,
  setErrorMessages,
  setLoadingChatMessages,
  setLoadingMessages,
  setLoadingMessagesEnd,
  setMessages,
} from './reducer';
import { store } from '../../../redux/store';
import { toast } from 'sonner';
import { generateUniqueId } from '../../../utils/helper';
import { Message } from '../../../interfaces/components/chatList';
import { throttle } from 'lodash';
import { endSession, startSession } from '../../auth/login/actions';
import { services } from '../../../api';
import { api } from '../../../api/api';
import moment from 'moment';
import { getLocalData, setLocalData } from '../../../utils/storage';
import { keys } from '../../../api/keys';
import { encryptUrlParam } from '../../../utils/security';
import { finishLoading, startLoading } from '../../../connected-components/loading/actions';
import process from 'node:process';

/**
 * Global declaration for stream controller
 * Allows aborting ongoing stream requests
 */
declare global {
  interface Window {
    streamController?: AbortController;
  }
}

/**
 * Submits a question to the AI and processes streaming response
 * The backend returns newline-delimited JSON objects (NDJSON)
 * accumulating chunks in a buffer and splitting them on "\n".
 *
 * @param {QNAStreamingForm} body - The question data containing user query and context
 * @return {Promise<void>} Resolves when the streaming is complete
 */
export const submitQNAStreaming = async (body: QNAStreamingForm) => {
  const id = generateUniqueId('assistant');
  try {
    if (!body.chat_id) {
      delete body.chat_id;
    }
    const { messages } = store.getState().chat || {};
    const newMessages: Message[] = [
      { id: generateUniqueId('user'), content: body.question, role: 'user' },
      ...(messages || []),
    ];
    store.dispatch(setMessages({ messages: newMessages }));
    store.dispatch(setLoadingMessages());

    // Abort any previous stream
    if (window.streamController) {
      window.streamController.abort();
    }
    window.streamController = new AbortController();
    const response = await fetch(api.qna.askStreaming, {
      signal: window.streamController.signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${store.getState().auth.currentUser.access_token}`,
      },
      body: JSON.stringify(body),
    });
    if (response.status === 401) {
      await endSession();
      throw new Error('Unauthorized access. Please login again.');
    }
    if (!response.body) {
      throw new Error('ReadableStream not supported');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let tempMessage = '';
    const metadata: any = {};

    /**
     * Throttled version of dispatch to prevent too many UI updates
     */
    const throttledDispatch = throttle((updatedMessages) => {
      store.dispatch(setMessages({ messages: updatedMessages }));
    }, 100);

    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Accumulate and decode the current chunk
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // Keep the last partial line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim()) continue;
        const parsedObj = JSON.parse(line);
        if (parsedObj.error) throw new Error(parsedObj.error);
        // Process text chunks
        if (parsedObj.text) {
          tempMessage += parsedObj.text;
        }
        // Append references to metadata.references if present
        if (parsedObj.references) {
          // If metadata.references already exists, append; otherwise, initialize it.
          metadata.references = metadata.references
            ? [...metadata.references, ...parsedObj.references]
            : [...parsedObj.references];
        }
        // Process metadata chunks (like chat_id info)
        if (parsedObj.chat_id) {
          metadata.chat_id = parsedObj.chat_id;
          metadata.conversation_title = parsedObj.conversation_title;
          metadata.dev_message_id = parsedObj.dev_message_id;
          if (!body.chat_id) {
            store.dispatch(
              createNewChat({
                id: parsedObj.chat_id,
                title: parsedObj.conversation_title || 'Untitled',
                created_at: moment().format('YYYY-MM-DD[T]HH:mm:ss'),
                in_favorite: 0,
                layout: store.getState().chat?.selectedChatLayoutDirection || 'ltr',
              }),
            );
            window.history.pushState(
              {},
              ',',
              `chat/${encryptUrlParam(parsedObj.chat_id)}/${encryptUrlParam(store.getState().chat?.selectedChatLayoutDirection)}`,
            );
            let categories = await getLocalData(keys.CHAT_CATEGORIES);
            if (!categories) categories = {};
            categories[metadata.chat_id] = body.category;
            await setLocalData(keys.CHAT_CATEGORIES, categories);
          }
        }

        // Update messages with the current accumulated text and metadata
        const { messages } = store.getState().chat || {};
        const tempMessages = messages?.filter((m) => m.id !== id) || [];
        const updatedMessages = [
          {
            id,
            content: tempMessage,
            role: 'assistant',
            ...(Object.keys(metadata).length > 0 ? metadata : {}),
          },
          ...tempMessages,
        ];
        throttledDispatch(updatedMessages);
      }
    }

    throttledDispatch.flush();
  } catch (error: any) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('QNA FAIL: ', error);
    }
    if (!error.message.includes('BodyStreamBuffer was aborted')) {
      store.dispatch(setErrorMessages());
      toast.error(tr('app.error'));
    }
    if (!window.streamController?.signal?.aborted) {
      const { messages } = store.getState().chat || {};
      const tempMessages = messages?.filter((m) => m.id !== id) || [];
      const updatedMessages: any[] = [
        {
          id,
          content:
            process.env.REACT_APP_DEBUG === 'true' ? error.message : 'The server is busy. Please try again later.',
          role: 'assistant',
        },
        ...tempMessages,
      ];
      store.dispatch(setMessages({ messages: updatedMessages }));
    }
  } finally {
    store.dispatch(setLoadingMessagesEnd());
    if (window.streamController) {
      window.streamController.abort();
    }
  }
};

/**
 * Fetches available chat categories from the API
 *
 * @returns {Promise<any>} Promise resolving to categories list
 */
export const getCategories = async () => {
  try {
    const res = await services.qna.getCategories();
    if (!res) {
      throw new Error('Error Fetching Categories');
    }
    if (Array.isArray(res.data)) {
      const { currentUser } = store.getState().auth || {};
      const tempCurrentUser = {
        ...currentUser,
        categories: res.data,
      };

      await startSession(tempCurrentUser);
    }
  } catch (error: any) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.info('Fetching Categories FAIL: ', error);
    }
  }
};

/**
 * Fetches chat history with optional filtering parameters
 *
 * @param {number} [start=0] - Starting index for pagination
 * @param {number} [length=50] - Number of chats to fetch
 * @param {string} [search=''] - Search term for filtering chats
 * @param {number} [favourite=0] - Filter by favorite status (1 for favorites only)
 * @param {string} [tab_id] - Filter by tab ID
 * @returns {Promise<{ success: boolean; chats: ChatItemType[] }>} Object containing success status and chats array
 */
export const getChatHistory = async (
  start: number = 0,
  length: number = 50,
  search: string = '',
  favourite: number = 0,
  tab_id?: string,
): Promise<{ success: boolean; chats: ChatItemType[] }> => {
  try {
    const response = await services.chats.getChats({
      start,
      length,
      search,
      favourite,
      tab_id,
    });
    return {
      success: true,
      chats: response.data.chats,
    };
  } catch (error) {
    return {
      success: false,
      chats: [],
    };
  }
};

/**
 * Fetches developer messages for a specific chat
 *
 * @param {number|string} id - Chat ID to fetch developer messages for
 * @returns {Promise<DevMessageItem[]>} Array of developer messages
 */
export const getDevMessages = async (id: number | string): Promise<DevMessageItem[]> => {
  try {
    const res = await services.qna.getDevMessages(id);
    if (res.data) {
      return res.data;
    }
    return [];
  } catch (ex) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.error(ex);
    }
    return [];
  }
};

/**
 * Fetches message history for a specific chat
 *
 * @param {number} chatId - ID of the chat to fetch messages for
 * @param {number} [start=0] - Starting index for pagination
 * @param {number} [length=50] - Number of messages to fetch
 * @param {string} [search=''] - Search term for filtering messages
 * @returns {Promise<{ messages: ChatMessageItemType[]; metadata: LinksMetaData }>} Object containing messages and metadata
 */
export const getMessagesHistory = async (
  chatId: number,
  start: number = 0,
  length: number = 50,
  search: string = '',
): Promise<{ messages: ChatMessageItemType[]; metadata: LinksMetaData }> => {
  try {
    store.dispatch(setLoadingChatMessages(true));
    const params: GetChatMessagesForm = { id: chatId, start, length, search };
    const res = await services.qna.getChatMessages(params);
    if (res.data) {
      return {
        messages: [...res.data.messages],
        metadata: res.data?.links_meta,
      };
    }
    return {
      messages: [],
      metadata: {} as LinksMetaData,
    };
  } catch (ex) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.error(ex);
    }
    return {
      messages: [],
      metadata: {} as LinksMetaData,
    };
  }
};

/**
 * Shares a chat with others by creating a public link
 *
 * @param {string} id - ID of the chat to share
 * @returns {Promise<string>} The shared chat URL
 */
export const shareChat = async (id: string) => {
  try {
    startLoading();
    const res = await services.share.shareChat(id);
    if (res.data.token) {
      const shareUrl = `${window.location.origin}/shared/chats/${res.data.token}`;
      await navigator?.clipboard?.writeText(shareUrl);
      toast.success(tr('chat.shareLinkCopied'));
    } else {
      toast.error(tr('chat.shareError'));
    }
  } catch (ex) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.error(ex);
    }
  } finally {
    finishLoading();
  }
};

/**
 * Toggles the favorite status of a chat
 *
 * @param {number} chatId - ID of the chat to toggle favorite status
 * @param {boolean} inFavorite - New favorite status
 * @param {string} [tab_id] - Optional tab ID for filtering
 * @returns {Promise<boolean>} Whether the operation was successful
 */
export const toggleChatFavorite = async (chatId: number, inFavorite: boolean, tab_id?: string): Promise<boolean> => {
  try {
    startLoading();
    const res = await services.chats.setFavorite(chatId, inFavorite, tab_id);
    return res.code === 200;
  } catch (ex) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.error(ex);
    }
    return false;
  } finally {
    finishLoading();
  }
};

/**
 * Deletes a chat from history
 *
 * @param {number} id - ID of the chat to delete
 * @returns {Promise<boolean>} Whether the operation was successful
 */
export const deleteChat = async (id: number) => {
  try {
    startLoading();
    const res = await services.chats.removeChat(id);
    if (res.code === 200) {
      return true;
    }
    return false;
  } catch (ex) {
    return false;
  } finally {
    finishLoading();
  }
};
